// utils/currency/normalize.js
//
// The write-path coercer (`normalizeCurrency`, strict: throws coded errors)
// and the read-path shape (`readCurrency`, tolerant: never throws) for a
// currency element. Dependency-free and vendored into keepr-web (see
// registry.js for the rule).
//
// Element options consulted: { currencies, defaultCurrency, min, max,
// nonNegative }. `min` / `max` are MAJOR-unit numbers and bind the entered
// amount in its OWN currency -- there is no conversion (v1), so a bound is a
// bound on the number a person typed, whichever of the element's currencies
// they typed it in. They are therefore most meaningful on a one-currency
// element, and the card editor says so.
// Error codes: 'type' | 'invalid_currency' | 'range' (the row-validator
// vocabulary). An amount more precise than its currency ("1.234 USD") is a
// `type` error: the entry is not a USD amount at all.

'use strict';

const { getCurrency, allowedCodes, isCurrencyValue } = require('./registry');
const { parseCurrency, parseCurrencyDetailed, toMinor } = require('./parse');

function fail(code, message) {
	const err = new Error(message);
	err.code = code;
	return err;
}

function isEmpty(raw) {
	return raw === undefined || raw === null || raw === '' || (typeof raw === 'string' && raw.trim() === '');
}

const isFiniteNumber = v => typeof v === 'number' && Number.isFinite(v);

/**
 * normalizeCurrency(raw, options) -> { amount, currency } | null
 *
 * Null for empty input ('' / null / undefined). Throws an Error with `.code`
 * for anything that cannot be stored: not an amount, or more precise than
 * its currency ('type'); a currency that is unknown or outside the element's
 * `currencies` ('invalid_currency'); a negative amount under `nonNegative`,
 * one outside `min`/`max`, or one too large to store exactly ('range').
 */
function normalizeCurrency(raw, options) {
	if (isEmpty(raw)) return null;
	const opts = options || {};
	const parsed = parseCurrencyDetailed(raw, Object.assign({}, opts, { fraction: 'refuse' }));
	if (parsed.error) throw fail(parsed.error, parsed.message);

	const currency = getCurrency(parsed.currency);
	if (!currency) throw fail('invalid_currency', '"' + parsed.currency + '" is not a currency');
	const allowed = allowedCodes(opts);
	if (allowed.length && allowed.indexOf(currency.code) === -1) {
		throw fail('invalid_currency', currency.code + ' is not allowed for this element (' + allowed.join(', ') + ')');
	}
	if (opts.nonNegative === true && parsed.amount < 0) {
		throw fail('range', 'the amount cannot be negative');
	}
	if (isFiniteNumber(opts.min)) {
		const floor = toMinor(opts.min, currency.code, 'keep');
		if (floor !== null && parsed.amount < floor) throw fail('range', 'must be at least ' + opts.min + ' ' + currency.code);
	}
	if (isFiniteNumber(opts.max)) {
		const ceiling = toMinor(opts.max, currency.code, 'keep');
		if (ceiling !== null && parsed.amount > ceiling) throw fail('range', 'must be at most ' + opts.max + ' ' + currency.code);
	}
	return { amount: parsed.amount, currency: currency.code };
}

/**
 * readCurrency(raw, options) -> { amount, currency } | null
 *
 * The tolerant read side: a stored object comes back as-is when its code is
 * known (whether or not the element still allows it -- tightening the list
 * never hides a value on record); a bare number is major units in the
 * element's default currency (a half-migrated `number` element), rounded to
 * the currency's exponent; a string is parsed the same way; anything else,
 * including a stored object with an unknown code, is null. Never throws.
 */
function readCurrency(raw, options) {
	if (isCurrencyValue(raw)) {
		return getCurrency(raw.currency) ? { amount: raw.amount, currency: raw.currency } : null;
	}
	if (isEmpty(raw)) return null;
	if (typeof raw === 'number' || typeof raw === 'string' || (raw && typeof raw === 'object' && !Array.isArray(raw))) {
		return parseCurrency(raw, Object.assign({}, options || {}, { fraction: 'round' }));
	}
	return null;
}

module.exports = {
	normalizeCurrency,
	readCurrency,
};
